import * as Yup from "yup";
import {
  WIPRO_ENTITY_ID_LIST,
  PRESCRIPTION_QUESTION_ENTITY_ID_LIST,
  NESTLE_ENTITY_ID_LIST,
} from "../../../config/constants";

export const PRESCRIBED_PHARMACY_CONTRACT = "Prescribed_Pharmacy";
export const PHARMACY_CONTRACT = "Pharmacy";
export const VACCINATION_CONTRACT = "Vaccination";
const LocalStorageKeys = {
  CURRENT_PM_ENTITY_ID: "currentPmEntityId",
  PENNY_DROP_ENABLED: "pennyDropEnabled",
};

const typeErrorString = (action, label) => `Please ${action} a valid ${label}.`;
const ACTION = {
  SELECT: "select",
  ENTER: "enter",
};

const getFieldTypes = (formName, labels) => ({
  treatmentType: Yup.string().typeError(
    typeErrorString(ACTION.SELECT, labels["treatmentType"][formName]),
  ),
  otherTreatmentType: Yup.string().when("treatmentType", {
    is: (val) => val === "others",
    then: (schema) =>
      schema
        .required(
          typeErrorString(ACTION.ENTER, labels["otherTreatmentType"][formName]),
        )
        .typeError(
          typeErrorString(ACTION.ENTER, labels["otherTreatmentType"][formName]),
        ),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  pincode: Yup.string().test(
    "is-valid-pincode",
    typeErrorString(ACTION.ENTER, labels["pincode"][formName]),
    (value) => {
      if (!value) {
        return true;
      }
      return /^[1-9][0-9]{5}$/.test(value);
    },
  ),
  doctorSpeciality: Yup.string().typeError(
    typeErrorString(ACTION.SELECT, labels["doctorSpeciality"][formName]),
  ),
  centerName: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["centerName"][formName]),
  ),
  centerAddress: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["centerAddress"][formName]),
  ),
  doctorRegisteredNumber: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["doctorRegisteredNumber"][formName]),
  ),
  doctorName: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["doctorName"][formName]),
  ),
  illnessType: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["illnessType"][formName]),
  ),
  otherIllnessType: Yup.string().when("illnessType", {
    is: (val) => val === "others",
    then: (schema) =>
      schema
        .required(
          typeErrorString(ACTION.ENTER, labels["otherIllnessType"][formName]),
        )
        .typeError(
          typeErrorString(ACTION.ENTER, labels["otherIllnessType"][formName]),
        ),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  billNumber: Yup.string().typeError(
    typeErrorString(ACTION.ENTER, labels["billNumber"][formName]),
  ),
  billDate: Yup.string().typeError(
    typeErrorString(ACTION.SELECT, labels["billDate"][formName]),
  ),
  billAmount: Yup.number()
    .min(1)
    .integer()
    .typeError(typeErrorString(ACTION.ENTER, labels["billAmount"][formName])),
  hasPrescription: Yup.string().nullable(),
  prescriptionReason: Yup.string().when("hasPrescription", {
    is: (val) => val === "no",
    then: (schema) =>
      schema
        .required("Please select a reason")
        .typeError("Please select a reason"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
});

const isWiproNonMandatoryField = (field, formName, billDetailsConfig) => {
  try {
    const currentEntityId = localStorage.getItem(
      LocalStorageKeys.CURRENT_PM_ENTITY_ID,
    );
    if (!currentEntityId) return false;

    const isWiproEntity = WIPRO_ENTITY_ID_LIST.includes(
      Number(currentEntityId),
    );
    if (!isWiproEntity) return false;

    return (
      billDetailsConfig?.wiproNonMandatoryFields?.[field]?.includes(formName) ??
      false
    );
  } catch {
    return false;
  }
};

const isPrescriptionEntityNonMandatoryField = (field, formName) => {
  try {
    const currentEntityId = localStorage.getItem(
      LocalStorageKeys.CURRENT_PM_ENTITY_ID,
    );

    if (!currentEntityId) return false;

    const isPrescriptionEntity = PRESCRIPTION_QUESTION_ENTITY_ID_LIST.includes(
      Number(currentEntityId),
    );
    if (!isPrescriptionEntity) return false;

    if (field !== "doctorRegisteredNumber") return false;

    const nonMandatoryCategories = [
      "Prescribed_Diagnostics(Lab_Test)",
      "Inclinic_Doctor_Consultation",
      "Prescribed_Pharmacy",
      "Pharmacy",
    ];

    const result = nonMandatoryCategories.includes(formName);
    return result;
  } catch (error) {
    return false;
  }
};

const isPrescriptionQuestionEntity = () => {
  try {
    const currentEntityId = localStorage.getItem(
      LocalStorageKeys.CURRENT_PM_ENTITY_ID,
    );
    if (!currentEntityId) return false;
    return PRESCRIPTION_QUESTION_ENTITY_ID_LIST.includes(
      Number(currentEntityId),
    );
  } catch {
    return false;
  }
};

const generateValidationSchema = (formName, billDetailsConfig) => {
  if (formName) {
    const fieldTypes = getFieldTypes(
      formName,
      billDetailsConfig.contractWiseFieldLabels,
    );

    const currentEntityId = localStorage.getItem(
      LocalStorageKeys.CURRENT_PM_ENTITY_ID,
    );
    const isNestleEntity = NESTLE_ENTITY_ID_LIST.includes(
      Number(currentEntityId),
    );

    const schemaShape = Object.keys(fieldTypes).reduce((acc, field) => {
      const isFieldInForm =
        billDetailsConfig.contractBasedFields[field] &&
        billDetailsConfig.contractBasedFields[field].includes(formName);

      let isRequired =
        billDetailsConfig.requiredFields[field] &&
        billDetailsConfig.requiredFields[field].includes(formName);

      if (
        isRequired &&
        isWiproNonMandatoryField(field, formName, billDetailsConfig)
      ) {
        isRequired = false;
      }

      if (
        isRequired &&
        isPrescriptionEntityNonMandatoryField(field, formName)
      ) {
        isRequired = false;
      }

      if (isFieldInForm) {
        // Special handling for doctorName in Nestle entity - it has conditional validation based on hasPrescription
        if (
          field === "doctorName" &&
          isNestleEntity &&
          (formName === PRESCRIBED_PHARMACY_CONTRACT ||
            formName === PHARMACY_CONTRACT)
        ) {
          acc[field] = fieldTypes[field]
            .label(billDetailsConfig.contractWiseFieldLabels[field][formName])
            .when("hasPrescription", {
              is: "yes",
              then: (schema) =>
                schema.required(
                  "Please enter " +
                    billDetailsConfig.contractWiseFieldLabels[field][formName],
                ),
              otherwise: (schema) => schema.nullable().notRequired(),
            });
        } else {
          acc[field] = isRequired
            ? fieldTypes[field]
                .label(
                  billDetailsConfig.contractWiseFieldLabels[field][formName],
                )
                .required()
            : fieldTypes[field]
                .label(
                  billDetailsConfig.contractWiseFieldLabels[field][formName],
                )
                .nullable();
        }
      }

      return acc;
    }, {});

    // Add hasPrescription + prescriptionReason validation for the prescription-question entity
    if (
      (formName === PRESCRIBED_PHARMACY_CONTRACT ||
        formName === PHARMACY_CONTRACT) &&
      isPrescriptionQuestionEntity()
    ) {
      schemaShape["hasPrescription"] = Yup.string()
        .required("Please indicate if you have a prescription")
        .typeError("Please indicate if you have a prescription");
      schemaShape["prescriptionReason"] = Yup.string().when("hasPrescription", {
        is: (val) => val === "no",
        then: (schema) =>
          schema
            .required(
              "Please select a reason for non-availability of prescription",
            )
            .typeError(
              "Please select a reason for non-availability of prescription",
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      });
    }

    return Yup.object().shape(schemaShape);
  }
};

export const hasOptionalField = (formName, billDetailsConfig) => {
  const fieldTypes = getFieldTypes(
    formName,
    billDetailsConfig.contractWiseFieldLabels,
  );
  return Object.keys(fieldTypes).some((field) => {
    const isFieldInForm =
      billDetailsConfig.contractBasedFields[field] &&
      billDetailsConfig.contractBasedFields[field].includes(formName);

    let isRequired =
      billDetailsConfig.requiredFields[field] &&
      billDetailsConfig.requiredFields[field].includes(formName);

    if (
      isRequired &&
      isWiproNonMandatoryField(field, formName, billDetailsConfig)
    ) {
      isRequired = false;
    }

    if (isRequired && isPrescriptionEntityNonMandatoryField(field, formName)) {
      isRequired = false;
    }

    if (isFieldInForm) {
      return !isRequired;
    }
    return false;
  });
};

/**
 * Check if a specific field is required for given form/bill type
 * Takes into account Wipro entity rules
 */
export const isFieldRequired = (fieldName, formName, billDetailsConfig) => {
  try {
    const isRequired =
      billDetailsConfig?.requiredFields?.[fieldName]?.includes(formName) ??
      false;
    return (
      isRequired &&
      !isWiproNonMandatoryField(fieldName, formName, billDetailsConfig) &&
      !isPrescriptionEntityNonMandatoryField(fieldName, formName)
    );
  } catch {
    return false;
  }
};

export const formNameMapper = {
  Doctor_consultation_In_Clinic: "Doctor consultation - In Clinic",
  "Prescribed_lab_tests_diagnostics_Radiology_(imaging tests)":
    "Prescribed lab tests/diagnostics - Radiology (imaging tests)",
  Pharmacy_Prescribed: "Pharmacy - Prescribed",
  Prescribed_Dental_treatments: "Prescribed Dental treatments",
  Pharmacy_OTC: "Pharmacy - OTC",
  Prescribed_Disability_Aids: "Prescribed Disability aids",
  "Vision_under_6_years_old/_Prescribed_disability_aids":
    "Vision under 6 years old/ Prescribed disability aids",
  Medical_Devices: "Medical Devices",
  Prescribed_Mental_Health_Therapy: "Prescribed Mental Health Therapy",
  Prescribed_Physiotheraphy: "Prescribed Physiotherapy",
  Vaccination_Cost: "Vaccination Cost",
  Doctor_Consultation_Online: "Doctor consultation - Online",
  "Other_prescribed_treatments_procedures_(outside_of _insurance_coverage)":
    "Other prescribed treatments/procedures (outside of insurance coverage)",
  Emergency_Pathology_Approved_only_for_Emergency_Case:
    "Emergency Pathology - Approved only for Emergency Case",
  "Smoking Prevention": "Smoking Prevention",
  "Medical Equipment": "Medical Equipment",
  "Other Treatments (Physio/Chiro/Psychiatry/Pschology)":
    "Other Treatments (Physio/Chiro/Psychiatry/Pschology)",
  Maternity: "Maternity",
};

/**
 * @param {string} formKey - Key for the formType
 */
export const getFormName = (formKey) => {
  if (formNameMapper[formKey]) {
    return formNameMapper[formKey];
  } else {
    return formKey.replace(/_wipro/i, "").replace(/_/g, " ");
  }
};

export const pageConfigurationPageIDs = {
  YOUR_CLAIMS_PAGE: "reim_your_claims_page",
  SUMMARY_PAGE: "reim_summary_page",
  CREATE_CLAIM_PAGE: "reim_create_claim_page",
};

export const pageConfigurationCarouselIDs = {
  REIM_SUMMARY_CAROUSEL: "reim_summary_carousel",
  REIM_YOUR_CLAIMS_CAROUSEL: "reim_your_claims_carousel",
  REIM_CREATE_CLAIM_CAROUSEL: "reim_create_claim_page_carousel",
};
export const BENEF_MAP = {
  SELF: "1",
};
export const VACCINATION_LABEL = "Vaccine Card/Doctor Certificate";
export const KEY_NOTES_POPUP_NESTLE = "nestleKeyNotesPopupShown";
export const KEY_NOTES_POPUP_ADOBE = "adobeKeyNotesPopupShown";
export const EMERGENCY_PATHOLOGY_CONTRACT =
  "Emergency_Pathology_Approved_only_for_Emergency_Case";
export const PM_ENTITY_ID = "pmentityid";
export const HEALTH_CHECK = "Health_Check";
export const DEPENDENTS_PREVENTIVE_HEALTH_CHECK_UP =
  "Dependents_Preventive_Health_Check_Up";
export const ANNUAL_HEALTH_CHECK_UP = "Annual_Health_Checkup";
export const VACCINATION = "Vaccination";
export const SELF = "Self";
export const USERS = "users";
export const ERROR_MAPPING_PATIENT_NAME_TO_RELATION =
  "Error mapping patient name to relation:";
export { LocalStorageKeys, generateValidationSchema };
